import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Calendar as CalendarIcon, ShoppingCart, PlusCircle, Search, LogOut, Home, Book, Users, UserCircle, Menu, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Auth from '@/components/Auth';
import Logo from '@/components/Logo';
import LogoUpload from '@/components/LogoUpload';
import RecipeList from '@/components/RecipeList';
import AddRecipe from '@/components/AddRecipe';
import Calendar from '@/components/Calendar';
import GroceryList from '@/components/GroceryList';
import Profile from '@/components/Profile';
import CommunityFeed from '@/components/CommunityFeed';
import UserSearch from '@/components/UserSearch';
import { Recipe } from '@/types';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/utils';

interface CookedRecipe {
  id: string;
  cooked_at: string;
  recipes: {
    id: string;
    name: string;
    image_url: string | null;
  };
}

export default function App() {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<'home' | 'recipes' | 'add' | 'calendar' | 'grocery' | 'profile' | 'community' | 'search'>('home');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recentlyCooked, setRecentlyCooked] = useState<CookedRecipe[]>([]);
  const [groceryItemCount, setGroceryItemCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadRecipes(),
        loadRecentlyCooked(),
        loadGroceryItemCount()
      ]);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      const { data, error } = await supabase
        .from('recipes')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedRecipes = data.map(recipe => ({
        id: recipe.id,
        name: recipe.name,
        ingredients: recipe.ingredients,
        instructions: recipe.instructions,
        prepTime: recipe.prep_time || 0,
        cookTime: recipe.cook_time || 0,
        servings: recipe.servings || 1,
        imageUrl: recipe.image_url,
        source: recipe.source,
        tags: recipe.tags || [],
        isPublic: recipe.is_public || false,
        userId: recipe.user_id
      }));

      setRecipes(transformedRecipes);
    } catch (error) {
      console.error('Error loading recipes:', error);
    }
  };

  const loadRecentlyCooked = async () => {
    try {
      const { data, error } = await supabase
        .from('cooked_recipes')
        .select(`
          id,
          cooked_at,
          recipes (
            id,
            name,
            image_url
          )
        `)
        .eq('user_id', user?.id)
        .order('cooked_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentlyCooked(data || []);
    } catch (error) {
      console.error('Error loading recently cooked:', error);
    }
  };

  const loadGroceryItemCount = async () => {
    try {
      const { count, error } = await supabase
        .from('grocery_items')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user?.id)
        .eq('checked', false);

      if (error) throw error;
      setGroceryItemCount(count || 0);
    } catch (error) {
      console.error('Error loading grocery count:', error);
    }
  };

  if (!user) {
    return <Auth />;
  }

  const navigationItems = [
    { icon: Home, label: 'Home', tab: 'home' },
    { icon: Book, label: 'Recipes', tab: 'recipes' },
    { icon: PlusCircle, label: 'Add Recipe', tab: 'add' },
    { icon: CalendarIcon, label: 'Calendar', tab: 'calendar' },
    { icon: ShoppingCart, label: 'Grocery List', tab: 'grocery', count: groceryItemCount },
    { icon: Users, label: 'Community', tab: 'community' },
    { icon: Search, label: 'Search Users', tab: 'search' },
    { icon: UserCircle, label: 'Profile', tab: 'profile' }
  ];

  const mobileNavigationItems = [
    { icon: Home, label: 'Home', tab: 'home' },
    { icon: Book, label: 'Recipes', tab: 'recipes' },
    { icon: PlusCircle, label: 'Add', tab: 'add' },
    { icon: ShoppingCart, label: 'Grocery', tab: 'grocery', count: groceryItemCount },
    { icon: Users, label: 'Community', tab: 'community' }
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Desktop Sidebar */}
      <div className="desktop-nav">
        <div className="p-6">
          <Logo />
        </div>
        
        <nav className="mt-6 px-3">
          {navigationItems.map(({ icon: Icon, label, tab, count }) => (
            <Button
              key={tab}
              variant="ghost"
              className={`w-full justify-start gap-3 mb-1 ${
                activeTab === tab ? 'bg-primary-50 text-primary-600' : ''
              }`}
              onClick={() => setActiveTab(tab as any)}
            >
              <Icon className="h-5 w-5" />
              {label}
              {count !== undefined && count > 0 && (
                <span className="ml-auto bg-primary-100 text-primary-600 px-2 py-0.5 rounded-full text-xs">
                  {count}
                </span>
              )}
            </Button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => signOut()}
          >
            <LogOut className="h-5 w-5" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="mobile-nav">
          <div className="flex items-center justify-between px-4 py-2">
            <Logo />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </header>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden">
            <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-lg">
              <nav className="p-4">
                {navigationItems.map(({ icon: Icon, label, tab, count }) => (
                  <Button
                    key={tab}
                    variant="ghost"
                    className={`w-full justify-start gap-3 mb-1 ${
                      activeTab === tab ? 'bg-primary-50 text-primary-600' : ''
                    }`}
                    onClick={() => {
                      setActiveTab(tab as any);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Icon className="h-5 w-5" />
                    {label}
                    {count !== undefined && count > 0 && (
                      <span className="ml-auto bg-primary-100 text-primary-600 px-2 py-0.5 rounded-full text-xs">
                        {count}
                      </span>
                    )}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2 mt-4 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => signOut()}
                >
                  <LogOut className="h-5 w-5" />
                  Sign Out
                </Button>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {activeTab === 'home' && (
              <div className="space-y-6">
                <h1 className="text-2xl font-bold">Welcome Back!</h1>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div 
                    onClick={() => setActiveTab('recipes')}
                    className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">Your Recipes</h3>
                      <Book className="h-5 w-5 text-primary-600" />
                    </div>
                    <p className="text-3xl font-bold mb-2">{recipes.length}</p>
                    <p className="text-sm text-gray-500 mb-4">Total recipes in your collection</p>
                    <div className="flex items-center text-primary-600 text-sm">
                      View all recipes <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </div>
                  
                  <div 
                    onClick={() => setActiveTab('calendar')}
                    className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">Recently Cooked</h3>
                      <CalendarIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="space-y-3 mb-4">
                      {recentlyCooked.slice(0, 3).map((entry) => (
                        <div key={entry.id} className="flex items-center gap-3">
                          {entry.recipes.image_url ? (
                            <img
                              src={entry.recipes.image_url}
                              alt={entry.recipes.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                              <Book className="h-5 w-5 text-primary-600" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium">{entry.recipes.name}</p>
                            <p className="text-xs text-gray-500">
                              {formatDate(new Date(entry.cooked_at))}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center text-primary-600 text-sm">
                      View calendar <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </div>

                  <div 
                    onClick={() => setActiveTab('grocery')}
                    className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium">Shopping List</h3>
                      <ShoppingCart className="h-5 w-5 text-primary-600" />
                    </div>
                    <p className="text-3xl font-bold mb-2">{groceryItemCount}</p>
                    <p className="text-sm text-gray-500 mb-4">Items to buy</p>
                    <div className="flex items-center text-primary-600 text-sm">
                      View shopping list <ChevronRight className="h-4 w-4 ml-1" />
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-medium mb-6">Recent Activity</h3>
                  {recentlyCooked.length > 0 ? (
                    <div className="space-y-6">
                      {recentlyCooked.map((entry) => (
                        <div key={entry.id} className="flex items-start gap-4">
                          {entry.recipes.image_url ? (
                            <img
                              src={entry.recipes.image_url}
                              alt={entry.recipes.name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-primary-50 rounded-lg flex items-center justify-center">
                              <Book className="h-8 w-8 text-primary-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{entry.recipes.name}</p>
                            <p className="text-sm text-gray-500">
                              Cooked on {formatDate(new Date(entry.cooked_at))}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CalendarIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Recent Activity</h3>
                      <p className="text-gray-500 mb-6">
                        Start tracking your cooking by adding recipes to your calendar
                      </p>
                      <Button onClick={() => setActiveTab('calendar')}>
                        Open Calendar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'recipes' && <RecipeList recipes={recipes} />}
            {activeTab === 'add' && (
              <AddRecipe onAddRecipe={(recipe) => setRecipes([...recipes, recipe])} />
            )}
            {activeTab === 'calendar' && <Calendar recipes={recipes} />}
            {activeTab === 'grocery' && <GroceryList recipes={recipes} onItemsChange={loadGroceryItemCount} />}
            {activeTab === 'community' && (
              <div className="space-y-8">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h2 className="text-xl font-semibold mb-4">Find Users</h2>
                  <UserSearch />
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-4">Community Feed</h2>
                  <CommunityFeed />
                </div>
              </div>
            )}
            {activeTab === 'profile' && <Profile />}
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="mobile-nav-fixed">
          <div className="grid grid-cols-5 gap-1 px-2 py-2">
            {mobileNavigationItems.map(({ icon: Icon, label, tab, count }) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`flex flex-col items-center justify-center p-1 ${
                  activeTab === tab ? 'text-primary-600' : 'text-gray-600'
                }`}
              >
                <Icon className="h-6 w-6" />
                <span className="text-xs mt-1">{label}</span>
                {count !== undefined && count > 0 && (
                  <span className="absolute top-0 right-0 bg-primary-600 text-white text-xs rounded-full px-1.5">
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>
      </div>
      <LogoUpload />
    </div>
  );
}